/** A unit of work in a wizard that makes more than one API call. */
export interface StepDefinition {
  id: string;
  label: string;
}

export type StepStatus = 'pending' | 'running' | 'done' | 'failed';

export interface StepView extends StepDefinition {
  status: StepStatus;
  error?: string;
}

/** What has happened so far. Completed steps are never re-run, which is what makes a retry safe. */
export interface RunState {
  completed: string[];
  running?: string;
  failed?: { id: string; error: string };
}

export const NOT_STARTED: RunState = { completed: [] };

export function viewSteps(definitions: StepDefinition[], run: RunState): StepView[] {
  return definitions.map((definition) => {
    if (run.completed.includes(definition.id)) {
      return { ...definition, status: 'done' };
    }
    if (run.failed?.id === definition.id) {
      return { ...definition, status: 'failed', error: run.failed.error };
    }
    if (run.running === definition.id) {
      return { ...definition, status: 'running' };
    }
    return { ...definition, status: 'pending' };
  });
}

/** The first step still to do, so a retry resumes rather than starts again. */
export function nextStep(definitions: StepDefinition[], run: RunState): StepDefinition | undefined {
  return definitions.find((definition) => !run.completed.includes(definition.id));
}

export function isComplete(definitions: StepDefinition[], run: RunState): boolean {
  return !nextStep(definitions, run);
}

/** True once something has succeeded: the wizard is now repairing rather than creating. */
export function hasPartialProgress(run: RunState): boolean {
  return run.completed.length > 0;
}
